import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, Crown, Zap, Star, ArrowRight, X, 
  Users, Infinity, Award, Rocket, Heart
} from 'lucide-react';

/**
 * ActivityLimitModal - Friendly prompt shown when free users hit their daily activity limit
 * 
 * Props:
 * - isOpen: boolean - Whether the modal is visible
 * - onClose: function - Called when modal is dismissed
 * - childName: string - Name of the child who hit the limit
 * - activitiesCompleted: number - How many activities completed today
 * - dailyLimit: number - The daily limit (usually 5)
 */
const ActivityLimitModal = ({ 
  isOpen, 
  onClose, 
  childName = 'Explorer',
  activitiesCompleted = 5,
  dailyLimit = 5 
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    navigate('/billing');
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="relative max-w-lg w-full animate-in fade-in zoom-in duration-300">
        {/* Floating celebration particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full animate-float opacity-60"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                backgroundColor: ['#fbbf24', '#a78bfa', '#34d399', '#60a5fa'][i % 4],
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${3 + Math.random() * 2}s`,
              }}
            />
          ))}
        </div>

        {/* Main card */}
        <div className="relative bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl border-2 border-amber-500/30 shadow-2xl overflow-hidden">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white rounded-full hover:bg-slate-700/50 transition-all z-10"
            data-testid="limit-modal-close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header with celebration */}
          <div className="relative pt-8 pb-6 px-6 text-center bg-gradient-to-b from-amber-500/10 to-transparent">
            {/* Star burst decoration */}
            <div className="absolute top-4 left-1/2 -translate-x-1/2 w-32 h-32 bg-amber-500/20 rounded-full blur-3xl" />
            
            <div className="relative">
              {/* Trophy/Star icon */}
              <div className="w-20 h-20 mx-auto mb-4 relative">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full animate-pulse opacity-50" />
                <div className="relative w-full h-full bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                  <Star className="w-10 h-10 text-white fill-white" />
                </div>
                {/* Sparkle decorations */}
                <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-amber-300 animate-bounce" />
                <Sparkles className="absolute -bottom-1 -left-2 w-5 h-5 text-amber-400 animate-bounce delay-150" />
              </div>

              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                Great Job Today, {childName}!
              </h2>
              
              <div className="flex items-center justify-center gap-2 text-amber-300 mb-3">
                <Heart className="w-5 h-5 fill-amber-400 text-amber-400" />
                <span className="text-lg">{activitiesCompleted} activities completed!</span>
                <Heart className="w-5 h-5 fill-amber-400 text-amber-400" />
              </div>

              <p className="text-slate-300 text-base sm:text-lg max-w-sm mx-auto">
                You've reached today's free activity limit. 
                <span className="text-amber-300 font-medium"> Upgrade to continue with unlimited learning!</span>
              </p>
            </div>
          </div>

          {/* Plan options */}
          <div className="px-6 pb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {/* Family Plan */}
              <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-2xl p-4 hover:bg-indigo-500/20 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-500/30 flex items-center justify-center">
                    <Users className="w-5 h-5 text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">Family Plan</h3>
                    <p className="text-indigo-300 text-sm">$6.99/month</p>
                  </div>
                </div>
                <ul className="space-y-1.5 text-sm text-slate-300">
                  <li className="flex items-center gap-2">
                    <Infinity className="w-4 h-4 text-indigo-400" />
                    Unlimited activities
                  </li>
                  <li className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-indigo-400" />
                    Cancel anytime
                  </li>
                </ul>
              </div>

              {/* Founder Plan */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 hover:bg-amber-500/20 transition-colors relative overflow-hidden">
                {/* Limited badge */}
                <div className="absolute -top-1 -right-1">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500 text-white text-xs font-bold rounded-bl-lg rounded-tr-xl">
                    <Rocket className="w-3 h-3" />
                    LIMITED
                  </span>
                </div>
                
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500/30 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">Founder Lifetime</h3>
                    <p className="text-amber-300 text-sm">$39 one-time</p>
                  </div>
                </div>
                <ul className="space-y-1.5 text-sm text-slate-300">
                  <li className="flex items-center gap-2">
                    <Infinity className="w-4 h-4 text-amber-400" />
                    Lifetime access
                  </li>
                  <li className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-amber-400" />
                    Founder rewards
                  </li>
                </ul>
              </div>
            </div>

            {/* CTA Buttons */}
            <button
              onClick={handleUpgrade}
              className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-500/25"
              data-testid="upgrade-from-limit-btn"
            >
              <Crown className="w-5 h-5" />
              Unlock Unlimited Learning
              <ArrowRight className="w-5 h-5" />
            </button>

            <p className="text-center text-slate-500 text-sm mt-4">
              Come back tomorrow for 5 more free activities!
            </p>
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(180deg); }
        }
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        .delay-150 {
          animation-delay: 150ms;
        }
      `}</style>
    </div>
  );
};

export default ActivityLimitModal;
